import { ResumeColor } from '@/constants/scenarios';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet } from 'react-native';
import { Text } from '../text';
import { View } from './tw-wrappers';

interface LevelGaugeProps {
  value: number; // 0 to 100
  size?: number;
  strokeWidth?: number;
  containerBackgroundColor?: string;
}

/**
 * LevelGauge - A semicircular gauge with 3 colored segments (Green, Yellow, Red)
 * and a needle indicator.
 */
export const LevelGauge = ({
  value,
  size = 140,
  strokeWidth = 16,
  containerBackgroundColor = '#EFF6FF',
}: LevelGaugeProps) => {
  const { t } = useTranslation();
  const radius = size / 2;

  // Clamped value for the needle (0 to 100)
  const clampedValue = Math.min(100, Math.max(0, value));
  // Needle angle: -90 (0%) to 90 (100%)
  const needleAngle = (clampedValue / 100) * 180 - 90;

  const COLORS = {
    low: ResumeColor.GREEN,    // Green
    medium: ResumeColor.YELLOW, // Yellow
    high: ResumeColor.RED,   // Red
    needle: '#374151', // Dark Gray
    divider: 'rgba(255,255,255,0.4)',
  };

  /**
   * Renders a rotating slice within a half-width mask.
   * angle: rotation from -180 (hidden) to 0 (full)
   */
  const renderHalfSlice = (angle: number, isRight: boolean, color: string) => (
    <View
      style={{
        position: 'absolute',
        width: radius,
        height: size,
        left: isRight ? radius : 0,
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          width: size,
          height: size,
          left: isRight ? -radius : 0,
          transform: [{ rotate: `${angle}deg` }],
        }}
      >
        <View
          style={{
            width: radius,
            height: size,
            left: isRight ? radius : 0,
            backgroundColor: color,
          }}
        />
      </View>
    </View>
  );

  return (
    <View style={{ width: size, height: radius + 35, alignItems: 'center' }}>
      {/* Semicircle Container */}
      <View style={{ width: size, height: radius, overflow: 'hidden', position: 'relative' }}>
        {/* Background Layer (Red/High) */}
        <View style={{
          width: size,
          height: size,
          borderRadius: radius,
          // backgroundColor: COLORS.high,
          overflow: 'hidden',
        }}>
          {/* Middle Segment (Yellow) - covers from -90 to 30 deg */}
          {/* This is 120 degrees starting from left (-90) */}
          {/* In this coordinate system, 180 deg is full half-circle. 
              Left half is 0 to 180 (clockwise). 
              Wait, DonutChart logic: rotate -180 is hidden, 0 is full.
          */}

          {/* Let's simplify: 
              We use the Background for High (Right 60 deg).
              We use a Yellow slice for the middle 60 deg.
              We use a Green slice for the left 60 deg.
          */}

          {/* Left Half (0 to 180 deg) */}
          {/* Green: 0 to 60. Yellow: 60 to 120. Red: 120 to 180. */}
          {/* But our gauge is a TOP semicircle. 
              If we rotate the whole thing -90 deg, then:
              - Left is 0.
              - Middle is 60.
              - Right is 120.
          */}
          <View style={{ width: size, height: size, transform: [{ rotate: '-90deg' }] }}>
            {/* Base: High (Red) */}
            <View style={{ position: 'absolute', width: size - 1, height: size, backgroundColor: COLORS.high, borderRadius: radius }} />
            {/* Middle (Yellow): 0 to 120 deg (covers left and middle) */}
            {renderHalfSlice(120 - 180, true, COLORS.medium)}

            {/* Low (Green): 0 to 60 deg (covers left) */}
            {renderHalfSlice(60 - 180, true, COLORS.low)}
          </View>
        </View>

        {/* Dividers */}
        <View style={[styles.divider, { transform: [{ rotate: '-30deg' }], left: radius - 0.5, height: size }]} />
        <View style={[styles.divider, { transform: [{ rotate: '30deg' }], left: radius - 0.5, height: size }]} />

        {/* Inner Hole */}
        <View
          style={{
            position: 'absolute',
            top: strokeWidth,
            left: strokeWidth,
            width: size - strokeWidth * 2,
            height: size - strokeWidth * 2,
            borderRadius: radius - strokeWidth,
            backgroundColor: containerBackgroundColor,
            zIndex: 20,
          }}
        />

        {/* Needle */}
        <View
          style={{
            position: 'absolute',
            bottom: 0,
            left: radius - 1.5,
            width: 3,
            height: radius - 4,
            backgroundColor: COLORS.needle,
            borderRadius: 2,
            zIndex: 30,
            transform: [
              { translateY: (radius - 4) / 2 },
              { rotate: `${needleAngle}deg` },
              { translateY: -(radius - 4) / 2 }
            ],
          }}
        />

        {/* Needle Center Cap */}
        <View
          style={{
            position: 'absolute',
            bottom: -6,
            left: radius - 6,
            width: 12,
            height: 12,
            borderRadius: 6,
            backgroundColor: COLORS.needle,
            zIndex: 40,
            borderWidth: 2,
            borderColor: 'white',
          }}
        />
      </View>

      {/* Labels */}
      <View style={styles.labelsContainer}>
        <Text style={styles.labelText}>{t('common.levels.low', 'Bajo')}</Text>
        <Text style={styles.labelText}>{t('common.levels.medium', 'Medio')}</Text>
        <Text style={styles.labelText}>{t('common.levels.high', 'Alto')}</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  divider: {
    position: 'absolute',
    top: 0,
    width: 1,
    backgroundColor: 'rgba(255,255,255,0.4)',
    zIndex: 10,
  },
  labelsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 10,
    paddingHorizontal: 0,
  },
  labelText: {
    fontSize: 10,
    color: '#6a7282',
    fontWeight: '800',
    // textTransform: 'uppercase',
  },
});
